<template>
  <div id="email_funnel_graph">
    <ContainerInfoItem
      source="email_funnel_graph"
      :title="title"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <BarChart
        v-else-if="chartCategories && chartData"
        id="email_funnel_graph"
        primary-color="#48AF7D"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="email_funnel_graph"
        text="No email funnel data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  title: { type: String, default: "Email Funnel" },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const categoryMap = {
  email_sent: "Emails Sent",
  email_opened: "Email Opens",
  email_clicked: "Email Clicks",
  email_replied: "Emails Replied",
  email_bounced: "Emails Replied",
  email_positive_replied: "Positive Replies",
  ooo_replies: "Out of Office Replies",
};

const fetchEmailFunnelData = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      sequence_id: filters.value?.sequence?.id,
    };

    const response = await AnalyticService.V2.fetchSequenceEmailFunnel(params);

    if (response?.success && response?.data) {
      const data = response?.data;

      const stages = Object.keys(data).filter((key) => key in categoryMap);

      chartCategories.value = stages.map((key) => categoryMap[key]);
      chartData.value = [
        {
          name: "Email Engagement",
          data: stages.map((key) => data[key]),
          color: "#48AF7D",
        },
      ];
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching email funnel data: ",
      error
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(
  [range, filters, userIds, teamIds],
  () => {
    if (
      (range.value && userIds.value && teamIds.value) ||
      filters.value?.sequence?.id
    ) {
      fetchEmailFunnelData();
    }
  },
  { deep: true }
);

onMounted(() => {
  // fetchEmailFunnelData();
});

// useAnalyticsRefresh(fetchEmailFunnelData, [range, userIds, teamIds]);
</script>
